/**
 * SL-52845 / SL-52846 — send x-sl-client-referer on the XHR that make BE fire a
 * server-side event.
 *
 * BE reads http_referer from request.referer, which on an XHR is the page that
 * issued it rather than the previous page -- inverted against the client-side
 * events. Downstream traffic_channel_source reads an empty http_referer as
 * `direct`, so that inversion makes `direct` unreachable. (Agreed 2026-08-03.)
 * The OAuth logins are browser redirects and cannot carry the header at all.
 */

// BE looks the header up by string key, so a typo yields nil without raising.
// Lower case is what BE receives either way: HTTP/2 lower-cases header names.
export const CLIENT_REFERRER_HEADER = 'x-sl-client-referer';

// Query params carrying a replayable credential, stripped before the referrer
// reaches the event pipeline and whatever it retains.
// BE's own list omits confirmation_token, so this layer is the only one dropping it.
const SENSITIVE_QUERY_PARAMS = [
  'authorization_token',
  'reset_password_token',
  'confirmation_token',
];

// Empty is expected (typed address, new tab, rel=noreferrer) and sent as-is;
// BE stores null and still delivers the event.
export const getClientReferrer = () => {
  const referrer = document.referrer;

  // Skipping URL when there is nothing to strip avoids normalising the referrer.
  if (SENSITIVE_QUERY_PARAMS.every((param) => !referrer.includes(param))) {
    return referrer;
  }

  try {
    const url = new URL(referrer);

    SENSITIVE_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));

    return url.toString();
  } catch (error) {
    // Unparseable, and it mentions a credential: drop it rather than forward it.
    return '';
  }
};

/**
 * One entry per BE hook in [BE] Shoplytics Server Side Event v1.10, kept 1:1 so
 * the two lists reconcile line by line; clientReferrer.spec.js pins the
 * endpoints deliberately left out. Matching is by URL only, so the other verbs
 * of a hooked path match too -- inert, since BE fires on the write.
 */
const SS_EVENT_PATHS = [
  // api/carts#add_item / #update_item / #remove_item
  /^api\/merchants\/[^/]+\/cart\/items(?:\/|$)/,
  // api/carts#fast_checkout_cart
  /^api\/merchants\/[^/]+\/cart\/fast_checkout_cart$/,
  // api/v1/cart/items#create / #update / #destroy
  /^api\/v1\/cart\/items(?:\/|$)/,
  // Api::Orders#checkout
  /^api\/orders\/checkout$/,
  // CartController#items
  /^cart\/items$/,
  // api/sessions#create
  /^api\/users\/sign_in$/,
  // api/registrations#create -- anchored, because register_validation /
  // register_status / missing_fields share the prefix and have no hook.
  /^api\/users$/,
  // api/omniauth_callbacks#facebook -- the JS SDK popup posts a real XHR, its
  // 302 sibling under api/ cannot.
  /^users\/auth\/facebook\/callback$/,
  // Api::Passwords#update -- completing a reset signs the member in, and
  // registers them when is_new_member.
  /^api\/users\/password$/,
];

// Call sites write the path both with and without a leading slash, so it is
// stripped first. Absolute cross-origin URLs never match, which keeps the header
// off requests where it would force a CORS preflight.
export const isSsEventUrl = (url) => {
  if (typeof url !== 'string') {
    return false;
  }

  const path = url.replace(/^\//, '').split('?')[0];

  return SS_EVENT_PATHS.some((pattern) => pattern.test(path));
};

/**
 * For the axios call sites, which build their own headers object -- spread it in
 * so X-Requested-With / cacheKey survive. Not an interceptor: an axios.create()
 * instance snapshots axios.defaults with an empty interceptor chain, losing the
 * X-CSRF-Token and the 401 -> x-ajax-redirect-url handler that
 * entry/fast_checkout.js registers later.
 */
export const getClientReferrerHeader = () => ({
  [CLIENT_REFERRER_HEADER]: getClientReferrer(),
});

// For $http, whose interceptor chain has no such problem. Angular has already
// merged defaults.headers.common with the per-call headers by the time this
// runs, so setting one key never replaces the object.
export const applyClientReferrerHeader = (config) => {
  if (!isSsEventUrl(config.url)) {
    return config;
  }

  config.headers = config.headers || {};
  config.headers[CLIENT_REFERRER_HEADER] = getClientReferrer();

  return config;
};
